import React, { useState, useEffect, useRef, useMemo } from "react";
import { Customer } from "../../types/customer";
import { getAllCustomers } from "../../services/customerService";
import { useAuth } from "../../context/AuthContext";
import { useBrandTheme } from "../../context/BrandThemeContext";
import { debug } from "../../utils/debugLogger";

interface CustomerDropdownProps {
  onCustomerSelect: (customer: Customer | null) => void;
  idToken?: string; // Make idToken optional
  compact?: boolean; // Add compact mode for smaller headers
  selectedCustomerId?: string | null; // Add prop to control selected value
  skipBrandThemeUpdate?: boolean; // Skip updating brand theme when customer is selected
}

/** Matches dropdown row text (business name + location) for consistent sorting. */
function buildCustomerLabel(customer: Customer): string {
  let name = customer.business_name ?? "";
  if (customer.city && customer.state) {
    name += ` - ${customer.city}, ${customer.state}`;
  }
  return name;
}

function sortCustomersAlphabetically(customers: Customer[]): Customer[] {
  return [...customers].sort((a, b) => {
    const cmp = buildCustomerLabel(a).localeCompare(
      buildCustomerLabel(b),
      undefined,
      { sensitivity: "base" }
    );
    return cmp !== 0 ? cmp : a.customer_id.localeCompare(b.customer_id);
  });
}

const CustomerDropdown: React.FC<CustomerDropdownProps> = ({
  onCustomerSelect,
  idToken,
  compact = false,
  selectedCustomerId: controlledCustomerId,
  skipBrandThemeUpdate = false,
}) => {
  const { idToken: authToken, isLoading: authLoading } = useAuth();
  const { setCustomerId } = useBrandTheme();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [internalSelectedCustomerId, setInternalSelectedCustomerId] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevControlledCustomerIdRef = useRef<string | null | undefined>(
    undefined
  );
  const emptyWorkspaceDefaultAppliedRef = useRef(false);
  // Always-current ref so async fetch closure reads the latest controlled value
  const controlledCustomerIdLiveRef = useRef(controlledCustomerId);
  useEffect(() => { controlledCustomerIdLiveRef.current = controlledCustomerId; });

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [isOpen]);

  // Use controlled value if provided, otherwise use internal state
  const selectedCustomerId =
    controlledCustomerId !== undefined
      ? controlledCustomerId || ""
      : internalSelectedCustomerId;

  useEffect(() => {
    // Wait for auth to finish loading before fetching customers
    if (authLoading) return;

    const fetchCustomers = async () => {
      const token = idToken || authToken;
      if (!token) {
        setIsLoading(false);
        setError("No idToken available, cannot fetch customers.");
        return;
      }
      try {
        // A deactivated Brand (NOT_ACTIVE) is kept for history, never deleted
        // (SGL-313), but it is not somewhere the owner works: hide it here so a
        // duplicate or retired Brand does not sit next to the live one. Admin
        // views (Customer Manager, All Users) still list everything.
        const list = (await getAllCustomers(token)).filter((c) => c.status !== "NOT_ACTIVE");
        setCustomers(list);

        if (list.length > 0) {
          const first = sortCustomersAlphabetically(list)[0];
          if (controlledCustomerId === undefined && !internalSelectedCustomerId) {
            // Uncontrolled mode: pick first alphabetically
            setInternalSelectedCustomerId(first.customer_id);
            onCustomerSelect(first);
            if (!skipBrandThemeUpdate) setCustomerId(first.customer_id);
          } else if (controlledCustomerIdLiveRef.current === "") {
            // Controlled mode, empty workspace (first visit / cleared): pick first
            emptyWorkspaceDefaultAppliedRef.current = true;
            onCustomerSelect(first);
            if (!skipBrandThemeUpdate) setCustomerId(first.customer_id);
          } else {
            // A remembered Brand (URL ?customerId= / workspace) that is no longer
            // in the list: retired (NOT_ACTIVE) since the last visit, or one this
            // login holds no Seat on. Keeping it selected makes every Brand call
            // 403 and the app shows "Sign in required" for a Brand that is simply
            // gone. Fall back to the first visible Brand instead.
            const remembered = controlledCustomerIdLiveRef.current ?? internalSelectedCustomerId;
            const stillVisible = remembered && list.some((c) => c.customer_id === remembered);
            if (remembered && !stillVisible) {
              debug.warn(`Remembered Brand ${remembered} is not in this login's list; selecting ${first.customer_id}`);
              if (controlledCustomerId === undefined) setInternalSelectedCustomerId(first.customer_id);
              onCustomerSelect(first);
              if (!skipBrandThemeUpdate) setCustomerId(first.customer_id);
            }
          }
        }
      } catch (error) {
        debug.error("Error fetching customers:", error);
        setError("Failed to fetch customers.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchCustomers();
  }, [idToken, authToken, authLoading]);

  // Reset "pick default" when workspace goes from a real id → empty (e.g. invalid id cleared).
  useEffect(() => {
    const prev = prevControlledCustomerIdRef.current;
    prevControlledCustomerIdRef.current = controlledCustomerId;

    if (controlledCustomerId === undefined) {
      return;
    }
    if (controlledCustomerId !== "") {
      emptyWorkspaceDefaultAppliedRef.current = false;
      return;
    }

    const hadRealId =
      prev !== undefined &&
      prev !== null &&
      typeof prev === "string" &&
      prev.length > 0;
    if (hadRealId) {
      emptyWorkspaceDefaultAppliedRef.current = false;
    }
  }, [controlledCustomerId]);

  // Controlled + empty workspace (e.g. first visit): pick newest once lists load.
  useEffect(() => {
    if (controlledCustomerId === undefined) return;
    if (controlledCustomerId !== "") return;
    if (authLoading || isLoading || customers.length === 0) return;
    if (emptyWorkspaceDefaultAppliedRef.current) return;
    emptyWorkspaceDefaultAppliedRef.current = true;
    const first = sortCustomersAlphabetically(customers)[0];
    onCustomerSelect(first);
    if (!skipBrandThemeUpdate) {
      setCustomerId(first.customer_id);
    }
  }, [
    authLoading,
    controlledCustomerId,
    customers,
    isLoading,
    onCustomerSelect,
    setCustomerId,
    skipBrandThemeUpdate,
  ]);

  // Stored / controlled id no longer in list — clear workspace so UI can recover.
  useEffect(() => {
    if (controlledCustomerId === undefined || controlledCustomerId === "")
      return;
    if (authLoading || isLoading || customers.length === 0) return;
    const ok = customers.some((c) => c.customer_id === controlledCustomerId);
    if (!ok) {
      onCustomerSelect(null);
    }
  }, [
    authLoading,
    controlledCustomerId,
    customers,
    isLoading,
    onCustomerSelect,
  ]);

  const handleCustomChange = (customerId: string) => {
    if (controlledCustomerId === undefined) {
      setInternalSelectedCustomerId(customerId);
    }
    const selectedCustomer = customers.find(
      (customer) => customer.customer_id === customerId
    );
    if (selectedCustomer) {
      onCustomerSelect(selectedCustomer);
      if (!skipBrandThemeUpdate) {
        setCustomerId(customerId);
      }
    } else {
      onCustomerSelect(null);
      if (!skipBrandThemeUpdate) {
        setCustomerId(null);
      }
    }
    setIsOpen(false);
  };

  const getCustomerDisplayName = (customer?: Customer) => {
    if (!customer) return "Select a profile...";
    return buildCustomerLabel(customer);
  };

  const customersAlphabetical = useMemo(
    () => sortCustomersAlphabetically(customers),
    [customers]
  );

  const selectedCustomerObj = customers.find((c) => c.customer_id === selectedCustomerId);

  return (
    <div className={`${compact ? "mb-0" : "mb-6"} relative`} ref={dropdownRef}>
      {!compact && (
        <label
          htmlFor="customer-dropdown"
          className="mb-2 block font-medium text-theme-fg"
        >
          Select a Business Profile:
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id="customer-dropdown"
          onClick={() => !isLoading && setIsOpen(!isOpen)}
          disabled={isLoading}
          className={`w-full flex items-center justify-between rounded-lg border border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-left text-theme-fg transition-colors hover:bg-theme-secondary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary ${
            compact ? "p-2 text-sm" : "p-4"
          }`}
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-2 truncate">
            {isLoading && (
               <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary flex-shrink-0"></div>
            )}
            <span className="truncate">
              {isLoading ? "Loading profiles..." : getCustomerDisplayName(selectedCustomerObj)}
            </span>
          </div>
          <span className={`material-icons text-zinc-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`}>
            expand_more
          </span>
        </button>

        {isOpen && !isLoading && (
          <div className="absolute left-0 z-[100] mt-1 max-h-72 min-w-full w-max max-w-[350px] overflow-auto rounded-md border border-[var(--color-input-border)] bg-theme-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none custom-scrollbar">
            {customersAlphabetical.map((customer) => (
              <button
                key={customer.customer_id}
                type="button"
                className={`flex w-full cursor-pointer flex-col px-4 py-2 text-left transition-all duration-200 relative group hover:scale-[1.02] hover:z-10 ${
                  selectedCustomerId === customer.customer_id
                    ? "bg-brand-primary/10 hover:bg-brand-primary/20"
                    : "hover:bg-zinc-100 dark:hover:bg-zinc-800"
                }`}
                onClick={() => handleCustomChange(customer.customer_id)}
              >
                <span
                  className={`text-sm font-medium ${
                    selectedCustomerId === customer.customer_id
                      ? "text-brand-primary"
                      : "text-theme-fg"
                  }`}
                >
                  {customer.business_name}
                </span>
                {customer.city && customer.state && (
                  <span
                    className={`text-xs ${
                      selectedCustomerId === customer.customer_id
                        ? "text-brand-primary/80"
                        : "text-theme-fg-muted"
                    } mt-0.5`}
                  >
                    {customer.city}, {customer.state}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <div className="text-red-500 mt-2 text-sm">{error}</div>}
    </div>
  );
};

export default CustomerDropdown;
